import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Users } from "lucide-react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import DiscussionService from "../../../../../services/discussion.service";
import { cn } from "../../../../../lib/utils";
import {
  card,
  pageTitle,
  accentBarSection,
  motionFadeUp,
  motionPageTransition,
  pageBg,
} from "../../../../../lib/design-system";
import DiscussionList from "./DiscussionList";
import DiscussionDetail from "./DiscussionDetail";

const DiscussionForum = ({ courseID: courseIDProp }) => {
  const { user } = useAuth();
  const { courseID: courseIDParam } = useParams();
  const courseID = courseIDProp || courseIDParam;

  const isTeacher = user.role === "teacher";
  const [activeTab, setActiveTab] = useState(
    isTeacher ? "teacher" : "course"
  );
  const [discussions, setDiscussions] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [showNewDiscussionForm, setShowNewDiscussionForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDiscussions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let response;
      if (activeTab === "teacher") {
        response = await DiscussionService.getTeacherDiscussions();
      } else {
        if (!courseID) {
          setDiscussions([]);
          setLoading(false);
          return;
        }
        response = await DiscussionService.getCourseDiscussions(courseID);
      }
      setDiscussions(response.discussions || []);
    } catch (err) {
      setError(err.message || "Failed to fetch discussions");
    } finally {
      setLoading(false);
    }
  }, [activeTab, courseID]);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  const handleViewDiscussion = useCallback(async (discussionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await DiscussionService.getDiscussionById(discussionId);
      setSelectedDiscussion(response.discussion);
    } catch (err) {
      setError(err.message || "Failed to load discussion");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCreateDiscussion = useCallback(
    async (discussionData, attachments) => {
      try {
        setLoading(true);
        setError(null);
        if (activeTab === "teacher") {
          await DiscussionService.createTeacherDiscussion(
            discussionData,
            attachments
          );
        } else {
          if (!courseID) {
            setError("No course selected");
            setLoading(false);
            return;
          }
          await DiscussionService.createCourseDiscussion(
            courseID,
            discussionData,
            attachments
          );
        }
        setShowNewDiscussionForm(false);
        fetchDiscussions();
      } catch (err) {
        setError(err.message || "Failed to create discussion");
      } finally {
        setLoading(false);
      }
    },
    [activeTab, courseID, fetchDiscussions]
  );

  const handleDeleteDiscussion = useCallback(
    async (discussionId) => {
      if (!window.confirm("Are you sure you want to delete this discussion?"))
        return;
      try {
        setLoading(true);
        setError(null);
        await DiscussionService.deleteDiscussion(discussionId);
        if (selectedDiscussion && selectedDiscussion._id === discussionId) {
          setSelectedDiscussion(null);
        }
        fetchDiscussions();
      } catch (err) {
        setError(err.message || "Failed to delete discussion");
      } finally {
        setLoading(false);
      }
    },
    [selectedDiscussion, fetchDiscussions]
  );

  const handleAddComment = useCallback(
    async (text, attachments) => {
      try {
        setLoading(true);
        setError(null);
        await DiscussionService.addComment(
          selectedDiscussion._id,
          text,
          attachments
        );
        handleViewDiscussion(selectedDiscussion._id);
      } catch (err) {
        setError(err.message || "Failed to add comment");
      } finally {
        setLoading(false);
      }
    },
    [selectedDiscussion, handleViewDiscussion]
  );

  const handleAddReply = useCallback(
    async (commentId, text, attachments) => {
      try {
        setLoading(true);
        setError(null);
        await DiscussionService.addReply(
          selectedDiscussion._id,
          commentId,
          text,
          attachments
        );
        handleViewDiscussion(selectedDiscussion._id);
      } catch (err) {
        setError(err.message || "Failed to add reply");
      } finally {
        setLoading(false);
      }
    },
    [selectedDiscussion, handleViewDiscussion]
  );

  const handleDeleteComment = useCallback(
    async (commentId) => {
      if (!window.confirm("Are you sure you want to delete this comment?"))
        return;
      try {
        setLoading(true);
        setError(null);
        await DiscussionService.deleteComment(
          selectedDiscussion._id,
          commentId
        );
        handleViewDiscussion(selectedDiscussion._id);
      } catch (err) {
        setError(err.message || "Failed to delete comment");
      } finally {
        setLoading(false);
      }
    },
    [selectedDiscussion, handleViewDiscussion]
  );

  const tabs = [];
  if (isTeacher) {
    tabs.push({
      key: "teacher",
      label: "Teacher Forum",
      icon: <User className="w-4 h-4" />,
    });
  }
  tabs.push({
    key: "course",
    label: "Course Forum",
    icon: <Users className="w-4 h-4" />,
  });

  return (
    <motion.div {...motionFadeUp} className={cn(pageBg, "p-3 sm:p-4")}>
      <div className={cn(card, "p-4 sm:p-6")}>
        <h2 className={pageTitle}>Discussion Forums</h2>
        <div className={cn(accentBarSection, "mt-2 mb-5")} />

        {tabs.length > 1 && (
          <div className="flex gap-2 mb-5">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-sm border transition-colors",
                  activeTab === tab.key
                    ? "bg-accent1 text-white border-accent1"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                )}
                onClick={() => {
                  setActiveTab(tab.key);
                  setSelectedDiscussion(null);
                  setShowNewDiscussionForm(false);
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {selectedDiscussion ? (
            <DiscussionDetail
              key="detail"
              discussion={selectedDiscussion}
              onBack={() => setSelectedDiscussion(null)}
              onDelete={handleDeleteDiscussion}
              onAddComment={handleAddComment}
              onAddReply={handleAddReply}
              onDeleteComment={handleDeleteComment}
              loading={loading}
            />
          ) : (
            <DiscussionList
              key="list"
              discussions={discussions}
              onView={handleViewDiscussion}
              onDelete={handleDeleteDiscussion}
              onCreate={handleCreateDiscussion}
              loading={loading}
              error={error}
              activeTab={activeTab}
              showForm={showNewDiscussionForm}
              onToggleForm={() =>
                setShowNewDiscussionForm(!showNewDiscussionForm)
              }
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default DiscussionForum;
